<script setup lang="ts">
import * as z from 'zod'

definePageMeta({
  layout: false
})

const schema = z.object({
  email: z.email('Invalid email address')
})

type Schema = z.output<typeof schema>

const state = reactive<Partial<Schema>>({
  email: undefined
})

const route = useRoute()
const returnTo = (route.query.returnTo as string) || '/'
const loading = ref(false)
const serverError = ref<string | null>(null)

// Step 1 of the reset journey: send the emailed code, then hand off to the
// shared OTP page in reset mode (?flow=reset). The continuation token is held
// server-side via the na_pwreset cookie the endpoint sets.
async function onSubmit() {
  serverError.value = null
  if (!resetFormIsValid.value) return
  loading.value = true
  try {
    const res: any = await $fetch('/api/auth/forgot-password/start', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: { email: state.email }
    })
    return navigateTo({
      path: '/otp',
      query: { flow: 'reset', masked: res?.maskedEmail || '', len: res?.codeLength || 6, returnTo }
    })
  } catch (e: any) {
    serverError.value = e?.data?.statusMessage || e?.statusMessage
      || "We couldn't start a password reset. Please check the email and try again."
  } finally {
    loading.value = false
  }
}

watch(() => state.email, () => { if (serverError.value) serverError.value = null })

const resetFormValidation = computed(() => {
  return schema.safeParse(state)
})

const resetFormIsValid = computed(() => {
  return resetFormValidation.value.success
})

// const fieldErrors = computed(() => {
//   if (resetFormIsValid) return {}
//   return resetFormValidation.value.error.flatten().fieldErrors
// }
</script>

<template>
  <div
    class="md:bg-elevated/25 md:bg-gradient-to-tl md:from-[#193322]/100 from-20% to-[#0A5024]/100
         w-full min-h-screen flex flex-col items-center justify-start md:justify-center py-8"
  >
    <!-- bg-elevated/25 bg-gradient-to-tl from-[#193322]/100 from-20% to-[#0A5024]/100   style="background: linear-gradient(313deg, #193322 24.94%, #0A5024 99.55%);" -->

    <div class="flex flex-col items-center bg-white md:h-fit text-center px-23 py-16 max-[639px]:px-6 max-[639px]:py-12">
      <div class="flex flex-col justify-center items-center">
        <img
          alt="Azzad Logo"
          src="~/assets/img/Azzad Asset Logo Symbol Square.png"
          class="p-3 w-18 border-2 border-gray-200 rounded mb-4"
        >
        <h1 class="text-[1.75rem] pt-4 font-semibold font-playfair text-black tracking-wide">
          Hello there,
        </h1>
        <h1 class="text-[1.75rem] font-semibold font-playfair text-black tracking-wide">
          Forgot your password
        </h1>
        <p class="text-sm pt-4 font-light font-nunito text-black">
          No problem. Just enter your email address below -
        </p>
        <p class="text-sm pt-1 font-light font-nunito text-black">
          we'll send you a code to reset it
        </p>
      </div>
      <div class="mt-10 w-full">
        <UForm
          :schema="schema"
          :state="state"
          class="w-100 max-[500px]:w-full"
          @submit.prevent="onSubmit"
        >
          <div class="w-full border border-[#DBE0F1] rounded-xs overflow-hidden">
            <!-- Email row -->
            <div
              class="relative p-4 border"
              :class="resetFormIsValid
                ? 'bg-[#F3FBF5] border-[#284E36]'
                : 'bg-white border-transparent'
              "
            >
              <p class="text-[0.5rem] font-light text-[#455668] text-start">
                Email Address
              </p>
              <UInput
                v-model="state.email"
                title="Please enter your email address"
                type="email"
                variant="none"
                required
                class="w-full text-Color-Neutral-Tetsu-Iron pr-10"
                placeholder="Enter email"
                :ui="{ base: 'w-full !ring-0 !shadow-none !px-0 !py-1' }"
              />
            </div>
          </div>

          <p v-if="serverError" class="mt-3 text-start text-sm font-normal font-nunito text-[#CD0A1E]">
            {{ serverError }}
          </p>

          <UButton
            type="submit"
            class="cursor-pointer w-full h-15 justify-center rounded-xs mt-6 uppercase p-3 font-black tracking-wider bg-[#C79900] text-white disabled:bg-gray-300 disabled:text-white/70 disabled:cursor-not-allowed disabled:opacity-100"
            :disabled="loading || !resetFormIsValid"
          >
            <span
              class="justify-start text-Color-Generic-White text-base font-black font-nunito uppercase leading-5 tracking-wide"
            >
              {{ loading ? 'SENDING…' : 'SEND CODE' }}
            </span>
          </UButton>

          <div class="flex mt-6 justify-center">
            <p class="text-sm font-light font-nunito text-black">
              Remembered it?
              <NuxtLink to="/login" class="text-[#284E36] underline ml-1">Back to sign in</NuxtLink>
            </p>
          </div>

          <!--          <UButton -->
          <!--            type="submit" -->
          <!--            class="bg-[#C79900] w-full justify-center rounded-xs mt-10 uppercase" -->
          <!--            :disabled="!resetFormIsValid" -->
          <!--          > -->
          <!--            Send Link -->
          <!--          </UButton> -->
        </UForm>
      </div>
    </div>
    <div class="flex text-[0.84375rem] flex-col items-center justify-between mt-10 px-4 w-fit">
      <p class="mx-auto text-center">
        <a class="text-[#648972] font-light">By signing, you agree to the</a>
        <a
          href="https://azzadasset.com/wp-content/uploads/2023/10/Azzad-Terms-and-Conditions.pdf"
          class="text-[#56A975] underline"
        >
          Terms of Service
        </a>
        <a class="text-[#648972] font-light">
          and to occasionally receive emails from us.
        </a>
      </p>

      <p class="mx-auto text-center mt-3">
        <a class="text-[#648972] font-light"> Please read our </a>
        <a
          href="https://azzadasset.com/privacy-policy/"
          class="text-[#56A975] underline"
        >
          Privacy Policy
        </a>
        <a class="text-[#648972] font-light"> to learn more.</a>
      </p>
    </div>
    <!--    <div class="flex items-center justify-between mt-4 px-4 w-120"> -->
    <!--      <p class="text-xs mx-auto text-center"> -->
    <!--        <a class="text-[#648972] font-light">By signing, you agree to the</a> <a -->
    <!--          href="https://azzadasset.com/wp-content/uploads/2023/10/Azzad-Terms-and-Conditions.pdf" -->
    <!--          class="text-[#56A975] underline" -->
    <!--        >Terms of Service</a> <a class="text-[#648972] font-light">and to occasionally -->
    <!--          receive emails from us. Please read our </a> <a -->
    <!--          href="https://azzadasset.com/privacy-policy/" -->
    <!--          class="text-[#56A975] underline" -->
    <!--        >Privacy Policy</a> <a -->
    <!--          class="text-[#648972] font-light" -->
    <!--        > to learn more.</a> -->
    <!--      </p> -->
    <!--    </div> -->
  </div>
</template>

<style scoped>
</style>
